/* Premium luxury gift voucher card — landscape 1.6:1, front + back with flip,
   amount-driven colour themes, embossed hero amount, integrated QR seal.

   Consumed by:
     • GiftVoucherManagement.jsx  — inside the voucher detail dialog
     • GiftVoucherVerify.jsx      — public server-verified landing page
*/
import { useMemo, useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { RotateCw } from "lucide-react";

/* --------------------------------------------------------------- */
/* Colour themes — each amount tier gets its own premium palette.  */
/* Amount thresholds are intentionally simple: we lookup by the    */
/* (currency, tier) pair. Every palette shares the same layout —   */
/* only the fills change so the whole family feels collectible.    */
/* --------------------------------------------------------------- */
const GOLD_METALLIC =
  "linear-gradient(135deg,#e6c069 0%,#c99a3b 25%,#f5deac 50%,#b17f27 75%,#e6c069 100%)";
const CHAMPAGNE =
  "linear-gradient(135deg,#efdcb5 0%,#d3b57e 40%,#f5e4c1 60%,#c9a86a 100%)";

const THEMES = {
  "INR-500": {
    label: "Deep Chocolate Brown",
    bg: "radial-gradient(circle at 20% 15%,#5a2a13 0%,#3a1a08 40%,#2a1006 100%)",
    accent: GOLD_METALLIC,
    ink: "#f4d47c",
    inkSoft: "#c9a56b",
    stroke: "#8a5a24",
  },
  "INR-1000": {
    label: "Royal Burgundy",
    bg: "radial-gradient(circle at 20% 15%,#7a1f1f 0%,#4a0f10 45%,#2f0808 100%)",
    accent: GOLD_METALLIC,
    ink: "#f4d47c",
    inkSoft: "#e6a37a",
    stroke: "#a2542a",
  },
  "INR-2000": {
    label: "Midnight Black",
    bg: "radial-gradient(circle at 20% 15%,#20232b 0%,#0d0f14 40%,#050507 100%)",
    accent: GOLD_METALLIC,
    ink: "#f5deac",
    inkSoft: "#c9a56b",
    stroke: "#8a6a2a",
  },
  "AUD-50": {
    label: "Champagne Gold",
    bg: "radial-gradient(circle at 20% 15%,#c9a86a 0%,#8a6a2a 55%,#5b4416 100%)",
    accent: CHAMPAGNE,
    ink: "#3a2306",
    inkSoft: "#5b3d0d",
    stroke: "#3a2306",
  },
  "AUD-100": {
    label: "Royal Maroon",
    bg: "radial-gradient(circle at 20% 15%,#5a1a26 0%,#390d15 45%,#22060b 100%)",
    accent: GOLD_METALLIC,
    ink: "#f4d47c",
    inkSoft: "#e6a37a",
    stroke: "#a2542a",
  },
  "AUD-150": {
    label: "Emerald Green",
    bg: "radial-gradient(circle at 20% 15%,#0f4d3a 0%,#053021 40%,#021711 100%)",
    accent: GOLD_METALLIC,
    ink: "#f4d47c",
    inkSoft: "#c9a56b",
    stroke: "#8a5a24",
  },
  "AUD-200": {
    label: "Royal Navy",
    bg: "radial-gradient(circle at 20% 15%,#0f2145 0%,#0a1633 40%,#050b1c 100%)",
    accent: GOLD_METALLIC,
    ink: "#f4d47c",
    inkSoft: "#c9a56b",
    stroke: "#8a5a24",
  },
  DEFAULT: {
    label: "Chocolate Brown",
    bg: "radial-gradient(circle at 20% 15%,#5a2a13 0%,#3a1a08 40%,#2a1006 100%)",
    accent: GOLD_METALLIC,
    ink: "#f4d47c",
    inkSoft: "#c9a56b",
    stroke: "#8a5a24",
  },
};

function pickTheme(currency, amount) {
  const key = `${(currency || "INR").toUpperCase()}-${Math.round(amount || 0)}`;
  if (THEMES[key]) return THEMES[key];
  const cur = (currency || "INR").toUpperCase();
  // Fall back by amount band per currency so any amount still gets flavour.
  if (cur === "INR") {
    if (amount >= 2000) return THEMES["INR-2000"];
    if (amount >= 1000) return THEMES["INR-1000"];
    return THEMES["INR-500"];
  }
  if (cur === "AUD") {
    if (amount >= 200) return THEMES["AUD-200"];
    if (amount >= 150) return THEMES["AUD-150"];
    if (amount >= 100) return THEMES["AUD-100"];
    return THEMES["AUD-50"];
  }
  return THEMES.DEFAULT;
}

const CURRENCY_SYMBOLS = {
  INR: "\u20B9", AUD: "A$", USD: "$", GBP: "\u00A3",
  EUR: "\u20AC", JPY: "\u00A5", SGD: "S$", NZD: "NZ$", CAD: "C$",
};
const money = (v, cur) => {
  const code = (cur || "INR").toUpperCase();
  const sym = CURRENCY_SYMBOLS[code] || `${code} `;
  const n = Number(v || 0).toLocaleString(undefined,
                    { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return `${sym}${n}`;
};

const LOGO_URL = "/pb-logo.png";

export default function PremiumVoucherCard({ v, verifyUrl, showFlipToggle = true, hideTechnical = false, overrideTheme = null, overrideBgImage = null }) {
  // API-driven theme lookup. When the admin has configured a template
  // for this (currency, amount) tier the PNG renderer will use those
  // exact colors — so the on-screen preview asks the same endpoint to
  // stay in sync. Falls back to the hardcoded THEMES map when the API
  // is unreachable (e.g. public verify page loading in a stale tab).
  const [dbTheme, setDbTheme] = useState(null);
  const [dbBgImage, setDbBgImage] = useState(null);

  useEffect(() => {
    if (overrideTheme) return;   // editor drives its own preview
    let cancelled = false;
    const cur = (v.currency || "INR").toUpperCase();
    const amt = Number(v.voucher_amount || 0);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gift-voucher/settings/templates/theme?currency=${encodeURIComponent(cur)}&amount=${amt}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => {
        if (cancelled || !t) return;
        setDbTheme({
          label: t.name || "Custom",
          bg: `radial-gradient(circle at 20% 15%,${t.bg_color_1} 0%,${t.bg_color_2} 45%,${t.bg_color_3} 100%)`,
          accent: `linear-gradient(135deg,${t.accent_color} 0%,${t.accent2_color} 55%,${t.accent_color} 100%)`,
          ink: t.ink_color,
          inkSoft: t.ink_soft_color,
          stroke: t.stroke_color,
        });
        setDbBgImage(t.background_image_base64 || null);
      })
      .catch(() => { /* silent — fallback below */ });
    return () => { cancelled = true; };
  }, [v.currency, v.voucher_amount, overrideTheme]);

  const theme = useMemo(() => {
    if (overrideTheme) return overrideTheme;
    if (dbTheme) return dbTheme;
    return pickTheme(v.currency, v.voucher_amount);
  }, [overrideTheme, dbTheme, v.currency, v.voucher_amount]);

  const bgImage = overrideBgImage ?? dbBgImage;
  const [flipped, setFlipped] = useState(false);
  // Use the server-provided verify link when available; the ManagementDialog
  // passes it in. For the public verify page we already ARE the verify page,
  // so a fallback empty QR is fine.
  const qrPayload = verifyUrl || `${v.voucher_number || "PBGV"}|${v.sig || ""}`;

  return (
    <div className="w-full" data-testid="premium-voucher-card">
      <style>{`
        @keyframes pbShine {
          0%   { transform: translateX(-120%) skewX(-20deg); opacity: 0; }
          20%  { opacity: 0.55; }
          60%  { opacity: 0.35; }
          100% { transform: translateX(220%) skewX(-20deg); opacity: 0; }
        }
        .pb-card-wrap { perspective: 1600px; }
        .pb-card-inner {
          position: relative;
          width: 100%;
          aspect-ratio: 1.6 / 1;
          transition: transform 900ms cubic-bezier(.2,.85,.2,1);
          transform-style: preserve-3d;
        }
        .pb-card-inner.flipped { transform: rotateY(180deg); }
        .pb-face {
          position: absolute; inset: 0; backface-visibility: hidden;
          border-radius: 20px; overflow: hidden;
        }
        .pb-face.back { transform: rotateY(180deg); }
        .pb-shine {
          position: absolute; top: 0; left: 0; height: 100%; width: 40%;
          background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%);
          pointer-events: none;
          animation: pbShine 4200ms ease-in-out 700ms 1;
        }
      `}</style>

      {/* Landscape 1.6 : 1 aspect */}
      <div className="pb-card-wrap w-full">
        <div className={`pb-card-inner ${flipped ? "flipped" : ""}`}>
          {/* Front */}
          <div className="pb-face front"
            style={{
              background: theme.bg,
              boxShadow: `0 30px 60px -20px rgba(0,0,0,.55), inset 0 0 0 1px ${theme.stroke}55, inset 0 0 40px rgba(0,0,0,.35)`,
            }}>
            {bgImage && (
              <img src={bgImage} alt=""
                className="absolute inset-0 w-full h-full object-cover"
                style={{ pointerEvents: "none" }}
              />
            )}
            <FrontFace v={v} theme={theme} qrPayload={qrPayload} money={money} />
            <div className="pb-shine" />
            <CornerFlourish theme={theme} />
          </div>
          {/* Back */}
          <div className="pb-face back"
            style={{
              background: theme.bg,
              boxShadow: `0 30px 60px -20px rgba(0,0,0,.55), inset 0 0 0 1px ${theme.stroke}55, inset 0 0 40px rgba(0,0,0,.35)`,
            }}>
            {bgImage && (
              <img src={bgImage} alt=""
                className="absolute inset-0 w-full h-full object-cover"
                style={{ pointerEvents: "none" }}
              />
            )}
            <BackFace v={v} theme={theme} hideTechnical={hideTechnical} />
            <CornerFlourish theme={theme} />
          </div>
        </div>
      </div>

      {showFlipToggle && (
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <button
            onClick={() => setFlipped((f) => !f)}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 hover:bg-white/5 transition"
            data-testid="voucher-flip-btn"
          >
            <RotateCw className="w-3 h-3" />
            {flipped ? "Show front" : "Show reverse (Redeem instructions)"}
          </button>
          <span className="opacity-70">· {theme.label} edition</span>
        </div>
      )}
    </div>
  );
}


/* -------------------- FRONT FACE -------------------- */
function FrontFace({ v, theme, qrPayload, money }) {
  return (
    <div className="absolute inset-0 grid grid-cols-[1.35fr_1fr] gap-0 p-[4%]">
      {/* Left column — brand + hero amount + recipient details */}
      <div className="relative z-10 flex flex-col justify-between">
        {/* Logo hero */}
        <div className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="Purnabramha"
            className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-contain"
            style={{
              boxShadow: `0 0 0 2px ${theme.stroke}66, 0 8px 22px rgba(0,0,0,.5)`,
              background: "transparent",
            }}
          />
          <div>
            <div className="uppercase tracking-[0.35em] text-[9px] sm:text-[10px]"
              style={{ color: theme.inkSoft }}>Purnabramha</div>
            <div
              className="font-serif italic text-lg sm:text-2xl leading-tight"
              style={{
                backgroundImage: theme.accent,
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                textShadow: "0 1px 0 rgba(0,0,0,.35)",
                letterSpacing: "0.02em",
              }}
            >
              Gift Voucher
            </div>
            <div className="text-[9px] sm:text-[10px] italic mt-0.5"
              style={{ color: theme.inkSoft }}>
              A Gift of Authentic Maharashtrian Hospitality
            </div>
          </div>
        </div>

        {/* Hero amount */}
        <div className="my-2">
          <div className="text-[8px] sm:text-[9px] uppercase tracking-[0.4em] mb-1"
            style={{ color: theme.inkSoft }}>The Value Of</div>
          <div
            className="font-serif font-bold leading-none"
            style={{
              fontSize: "clamp(2.5rem, 7.5vw, 4.5rem)",
              backgroundImage: theme.accent,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              filter: "drop-shadow(0 3px 6px rgba(0,0,0,.55))",
              letterSpacing: "-0.01em",
            }}
            data-testid="voucher-hero-amount"
          >
            {money(v.voucher_amount, v.currency)}
          </div>
          <div className="mt-1 inline-flex items-center gap-1.5">
            <span
              className="inline-block h-[6px] w-[26px] rounded-sm"
              style={{ background: theme.accent }}
            />
            <span className="uppercase text-[8px] sm:text-[9px] tracking-[0.35em]"
              style={{ color: theme.inkSoft }}>One Time Use Only</span>
          </div>
        </div>

        {/* Recipient block */}
        <div className="text-[10px] sm:text-xs space-y-0.5" style={{ color: theme.ink }}>
          <Row label="Presented To" value={v.recipient_name} theme={theme} />
          <Row label="Gifted By" value={v.purchaser_name} theme={theme} />
          {v.occasion && <Row label="Occasion" value={v.occasion} theme={theme} />}
          {v.personal_message && (
            <div className="italic mt-1 line-clamp-2" style={{ color: theme.inkSoft }}>
              &ldquo;{v.personal_message}&rdquo;
            </div>
          )}
        </div>
      </div>

      {/* Right column — QR seal + voucher meta */}
      <div className="relative z-10 flex flex-col items-center justify-between gap-2">
        <div className="rounded-xl p-2 sm:p-2.5"
          style={{
            background: theme.accent,
            boxShadow: `0 6px 18px rgba(0,0,0,.4), inset 0 0 0 1px ${theme.stroke}`,
          }}>
          <div className="bg-white rounded-md p-1.5 sm:p-2">
            <QRCodeSVG
              value={qrPayload}
              size={110}
              level="M"
              includeMargin={false}
              className="w-[80px] h-[80px] sm:w-[110px] sm:h-[110px]"
            />
          </div>
          <div className="text-center mt-1 text-[8px] sm:text-[9px] font-semibold uppercase tracking-widest"
            style={{ color: "#4a1010" }}>Scan to Redeem</div>
        </div>

        <div className="w-full text-[9px] sm:text-[10px] space-y-0.5"
          style={{ color: theme.inkSoft }}>
          <MetaLine label="Voucher №" value={v.voucher_number} theme={theme} />
          <MetaLine label="Issued" value={(v.issued_at || "").slice(0, 10)} theme={theme} />
          <MetaLine label="Expires" value={(v.expires_at || "").slice(0, 10)} theme={theme} />
          <MetaLine label="Valid At" value={v.center} theme={theme} />
          <div className="pt-1 text-center text-[8px] uppercase tracking-[0.25em]"
            style={{ color: theme.inkSoft }}>purnabramha.com</div>
        </div>
      </div>
    </div>
  );
}


/* -------------------- BACK FACE -------------------- */
function BackFace({ v, theme, hideTechnical }) {
  return (
    <div className="absolute inset-0 p-[4%] grid grid-cols-2 gap-4">
      <div className="relative z-10">
        <div className="uppercase tracking-[0.35em] text-[10px] mb-1"
          style={{ color: theme.inkSoft }}>How To Redeem</div>
        <ol className="text-[10px] sm:text-xs space-y-1 list-decimal ml-4"
          style={{ color: theme.ink }}>
          <li>Present this voucher at the counter.</li>
          <li>Staff scans the QR code on the front.</li>
          <li>Voucher is verified online in real-time.</li>
          <li>Enjoy your meal at Purnabramha.</li>
        </ol>

        <div className="uppercase tracking-[0.35em] text-[10px] mt-3 mb-1"
          style={{ color: theme.inkSoft }}>Terms & Conditions</div>
        <ul className="text-[9px] sm:text-[10px] space-y-0.5 list-disc ml-4"
          style={{ color: theme.ink }}>
          <li>Valid for one-time use only.</li>
          <li>Cannot be exchanged for cash.</li>
          <li>Non-transferable.</li>
          <li>Lost vouchers cannot be replaced.</li>
          <li>Valid only after online verification.</li>
          <li>Management reserves the right to amend terms.</li>
        </ul>
      </div>

      <div className="relative z-10 flex flex-col justify-between">
        <div>
          <div className="uppercase tracking-[0.35em] text-[10px] mb-1"
            style={{ color: theme.inkSoft }}>Voucher Information</div>
          <div className="text-[10px] sm:text-xs space-y-0.5"
            style={{ color: theme.ink }}>
            <MetaLine label="Voucher №" value={v.voucher_number} theme={theme} />
            <MetaLine label="Value" value={money(v.voucher_amount, v.currency)} theme={theme} />
            <MetaLine label="Issued" value={(v.issued_at || "").slice(0, 10)} theme={theme} />
            <MetaLine label="Expires" value={(v.expires_at || "").slice(0, 10)} theme={theme} />
            <MetaLine label="Status" value={v.status || "ACTIVE"} theme={theme} />
            {v.voucher_id && !hideTechnical && (
              <MetaLine label="Voucher ID" value={v.voucher_id} theme={theme} mono />
            )}
          </div>
        </div>

        <div className="text-[9px] sm:text-[10px] mt-3 opacity-90 border-t pt-2"
          style={{ color: theme.inkSoft, borderColor: `${theme.stroke}55` }}>
          <div><strong style={{ color: theme.ink }}>Support:</strong> hello@purnabramha.com</div>
          <div><strong style={{ color: theme.ink }}>Web:</strong> purnabramha.com</div>
          <div className="mt-1 italic">India · USA · Australia · Japan</div>
        </div>
      </div>
    </div>
  );
}


/* -------------------- Small parts -------------------- */
function Row({ label, value, theme }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 uppercase text-[9px] tracking-[0.2em]"
        style={{ color: theme.inkSoft }}>{label}</span>
      <span className="font-medium truncate" style={{ color: theme.ink }}>{value || "—"}</span>
    </div>
  );
}

function MetaLine({ label, value, theme, mono }) {
  return (
    <div className="flex gap-2 justify-between">
      <span className="uppercase text-[8px] sm:text-[9px] tracking-[0.25em] shrink-0"
        style={{ color: theme.inkSoft }}>{label}</span>
      <span className={`truncate text-right ${mono ? "font-mono" : ""}`}
        style={{ color: theme.ink }}>{value || "—"}</span>
    </div>
  );
}

function CornerFlourish({ theme }) {
  // Four soft gold arcs at the corners — visual richness without clutter.
  return (
    <>
      <svg viewBox="0 0 60 60" className="absolute top-0 left-0 w-14 h-14 opacity-70">
        <path d="M4 30 Q 4 4 30 4" fill="none" stroke={theme.stroke} strokeWidth="1.4" />
        <circle cx="4" cy="30" r="1.6" fill={theme.stroke} />
        <circle cx="30" cy="4" r="1.6" fill={theme.stroke} />
      </svg>
      <svg viewBox="0 0 60 60" className="absolute top-0 right-0 w-14 h-14 opacity-70 rotate-90">
        <path d="M4 30 Q 4 4 30 4" fill="none" stroke={theme.stroke} strokeWidth="1.4" />
      </svg>
      <svg viewBox="0 0 60 60" className="absolute bottom-0 left-0 w-14 h-14 opacity-70 -rotate-90">
        <path d="M4 30 Q 4 4 30 4" fill="none" stroke={theme.stroke} strokeWidth="1.4" />
      </svg>
      <svg viewBox="0 0 60 60" className="absolute bottom-0 right-0 w-14 h-14 opacity-70 rotate-180">
        <path d="M4 30 Q 4 4 30 4" fill="none" stroke={theme.stroke} strokeWidth="1.4" />
      </svg>
    </>
  );
}
